define((require) => {
    "use strict";

    const _                = require("underscore");
    const EventDispatcher  = require("shared/util/EventDispatcher");
    const Gridsets         = require("services/snap/Gridsets");
    const Marionette       = require("marionette");
    const Preconditions    = require("shared/util/Preconditions");
    const SnapUtils        = require("util/SnapUtils");

    // Tools & Constants
    const SNAPLINE_NUDGE = 1;
    const THROTTLE_TIME  = 100;

    return Marionette.Behavior.extend({
        grid: null,

        offset: null,

        selectedElements: null,

        snappingEnabled: false,

        constructor(config, sceneEditor) {
            this._sceneEditor = sceneEditor;
            this._eventHandler = new EventDispatcher();
            _.bindAll(this, "selectWidget", "moveWidget", "dragEnd", "_buildGridlines", "_setSnappingEnabled");
            this.throttledMoveThis =  _.throttle(this.moveWidget, THROTTLE_TIME);
        },

        onRender() {
            // Attaching events onRender and not onShow, because the scenes are re-rendered when the size of an
            // experience is changed (but not re-shown).
            this._eventHandler
                .stopListening() // Make sure we don't attach the same events multiple times.
                .listenTo(this._sceneEditor.sceneWidgetCollectionView, {
                    "select_target": this.selectWidget,
                    "translating": this.throttledMoveThis,
                    "transforming_end": this.dragEnd
                })
                .listenTo(this._sceneEditor.viewScope.viewModel, "change:rulersShown", this._buildGridlines)
                .listenTo(this._sceneEditor.viewScope.viewModel, "change:snappingEnabled", this._setSnappingEnabled)
                .listenTo(this._sceneEditor.model.get("guidelines"), "add change remove", this._buildGridlines);
        },

        onShow() {
            const experienceModel = this._sceneEditor.experienceModel;
            const snappingEnabled = this._sceneEditor.viewScope.viewModel.get("snappingEnabled");
            this._setSnappingEnabled(experienceModel, snappingEnabled);
        },

        onClose() {
            this._eventHandler.stopListening();
        },

        // Sets selected widgets and builds the internal gridsets used for snapping
        selectWidget(selectedTElements, allTransformables, selectedTransformable) {
            this._setSelectedElements(selectedTransformable);

            this._buildGridlines();
        },

        // Performs the actual snapping. Throttled.
        moveWidget(selectedTElements, allTransformables, selectedTransformable) {
            if (!this.snappingEnabled) {
                return;
            }

            const snaplines = this.calculateSnaplinePosition(selectedTransformable);

            if (snaplines.offset.x === undefined && snaplines.offset.y === undefined) {
                this._hideAllElements();
            }

            if (snaplines.offset.x !== undefined) {
                this._showSnapline(snaplines.transformableCornersAndCenterOffset, snaplines.offset.x, "vertical");
            }

            if (snaplines.offset.y !== undefined) {
                this._showSnapline(snaplines.transformableCornersAndCenterOffset, snaplines.offset.y, "horizontal");
            }

            // For dragEnd
            this.offset = snaplines.offsetGrid;
        },

        // Called when dragging ends. Moves the widget into place if it is currently snapped
        dragEnd() {
            this._sceneEditor.trigger("isWidgetDragging", false);

            setTimeout(() => {
                this._hideAllElements();

                if (this.offset && _.some(this.offset)) {
                    const offsetX = this.offset.x ? this.offset.x.offset : 0;
                    const offsetY = this.offset.y ? this.offset.y.offset : 0;
                    this._sceneEditor.sceneWidgetCollectionView.activeLayerWidgetCollectionView().transformer.translate(offsetX, offsetY);
                }

                this.offset = {};
            }, THROTTLE_TIME + 50);
        },

        _setSnappingEnabled(experienceModel, snappingEnabled) {
            this.snappingEnabled = snappingEnabled;
        },

        _hideAllElements() {
            this._sceneEditor.ui.snapReplacement.hide();
            this._sceneEditor.ui.snapLineVertical.hide();
            this._sceneEditor.ui.snapLineHorizontal.hide();
            this._sceneEditor.ui.snapCross.hide();
            this._sceneEditor.ui.widgetContainer.removeClass("domElSnapped");
        },

        _getSceneSize() {
            const sceneSize = {
                width: this._sceneEditor.experienceModel.get("width"),
                height: this._sceneEditor.experienceModel.get("height")
            };

            // Side effect
            this._setCrossPosition(sceneSize);

            return sceneSize;
        },

        _setCrossPosition(sceneSize) {
            this._sceneEditor.ui.snapCross.css({
                left: Math.round(sceneSize.width / 2),
                top: Math.round(sceneSize.height / 2)
            });
        },

        _lineIsFromscene(coordinate, sceneSize, direction) {
            switch (direction) {
                case "vertical":
                    return coordinate === Math.round(sceneSize.width / 2);

                case "horizontal":
                    return coordinate === Math.round(sceneSize.height / 2);

                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - Direction '${direction}' not recognized.`);
                    break;
            }
        },

        _drawSnaplines(direction, coordinate, min, max) {
            const sceneSize = this._getSceneSize();

            switch (direction) {
                case "vertical":
                    this._sceneEditor.ui.snapLineVertical.css({
                        left: coordinate === sceneSize.width ? coordinate - SNAPLINE_NUDGE : coordinate,
                        top: min,
                        height: Math.min(max - min, sceneSize.height)
                    }).show();
                    break;

                case "horizontal":
                    this._sceneEditor.ui.snapLineHorizontal.css({
                        left: min,
                        width: Math.min(max - min, sceneSize.width),
                        top: coordinate === sceneSize.height ? coordinate - SNAPLINE_NUDGE : coordinate
                    }).show();
                    break;

                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - Direction '${direction}' not recognized.`);
                    break;
            }

            if (this._lineIsFromscene(coordinate, sceneSize, direction)) {
                this._sceneEditor.ui.snapCross.show();
            }

            this._sceneEditor.ui.widgetContainer.addClass("domElSnapped");
        },

        _getRotateCssObject(matrix) {
            return {transform: matrix.toString(), transformOrigin: "0 0"};
        },

        _showReplacement(matrix, width, height) {
            const css = _.extend({width, height}, this._getRotateCssObject(matrix));

            return this._sceneEditor.ui.snapReplacement.css(css).show();
        },

        /**
         * Public API for calculating the position the snaplines (and therefore widget) should be placed at.
         * @param {TElement} transformable element being transformed
         * @returns {Object} object containing properties about the snaplines to be applied
         * - transformableCornersAndCenterOffset: corners and center of the transformable element
         * - offset object containing `x` and `y` properties, which are the snaplines that are shown on those axes.
         * each key can be a `number` or `undefined` (if no snapline is applicable to be drawn)
         * - offsetGrid
         */
        calculateSnaplinePosition(transformable) {
            const offsetGrid      = this.grid.getOffset(transformable);
            const offsetMatrix    = this._getOffsetMatrix(offsetGrid, transformable.getMatrix());
            const width           = transformable.getWidth();
            const height          = transformable.getHeight();
            const replacement     = this._showReplacement(offsetMatrix, width, height);

            // Creating the snaplines
            const transformableCornersAndCenterOffset   = this._addCenter(SnapUtils.getRoundedCornerCoordinatesFromDom(replacement));
            const offsetX         = offsetGrid.x && offsetGrid.x.value + offsetGrid.x.offset;
            const offsetY         = offsetGrid.y && offsetGrid.y.value + offsetGrid.y.offset;

            return {
                transformableCornersAndCenterOffset,
                offset: {
                    x: offsetX,
                    y: offsetY
                },
                offsetGrid
            };
        },

        _addCenter(corners) {
            const xs  = _.pluck(corners, "x");
            const ys  = _.pluck(corners, "y");
            const x   = Math.round((_.min(xs) + _.max(xs)) / 2);
            const y   = Math.round((_.min(ys) + _.max(ys)) / 2);
            corners.push({x, y});

            return corners;
        },

        _showSnapline(offsetCorners, offset, direction) {
            const a = direction === "vertical" ? "x" : "y";
            const b = direction === "vertical" ? "y" : "x";

            const corners = offsetCorners.filter((o) => o[a] === offset);
            const allCorners = this.grid.getSet(direction, offset).concat(_.pluck(corners, b));

            this._drawSnaplines(direction, offset, _.min(allCorners), _.max(allCorners));
        },

        _getOffsetMatrix(offset, matrix) {
            if (offset.x) {
                matrix.x = Math.round(matrix.x + offset.x.offset);
            }

            if (offset.y) {
                matrix.y = Math.round(matrix.y + offset.y.offset);
            }

            return matrix;
        },

        _addElementToGrid(el) {
            if (!_.contains(this.selectedElements, el)) {
                this.grid.addElement(el);
            }
        },

        _buildGridlines() {
            this.grid = new Gridsets(this._getSceneSize());

            const allWidgetElements = this._sceneEditor.sceneWidgetCollectionView.activeLayerWidgetCollectionView().children.pluck("el");
            _.each(allWidgetElements, this._addElementToGrid, this);

            if (this._sceneEditor.viewScope.viewModel.get("rulersShown")) {
                this.grid.addGuidelines(this._sceneEditor.model.get("guidelines"));
            }
        },

        _setSelectedElements(transformables) {
            if (transformables) {
                this.selectedElements = transformables.domElem ?
                    [transformables.domElem] :                      // one selected
                    _.pluck(transformables.members, "domElem");     // multiple selected
            } else {
                this.selectedElements = [];                         // none selected
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/services/snap/Snap.js